import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { AppContext as CurrencyContext } from '../../../apollo/wrap-root-element';
import { getFormatNumberWithCommas } from '../../../modules/timeline/components/EnterButton/utils';
import { getUserCountryCode } from '../../../utils/getusercountrycode';
import { PriceLoader } from '../../../utils/loader';

export const currencySymbolMap = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  AUD: '$',
  NZD: '$',
  CAD: '$',
};

const countryToCurrencyMap = {
  US: 'USD',
  GB: 'GBP',
  AU: 'AUD',
  NZ: 'NZD',
  CA: 'CAD',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
};

const CurrencySwitcher = React.memo(({ price, isCommaSeparated, caller }) => {
  const { currency: selectedCurrency, currenciesRate, setCurrency } = useContext(CurrencyContext);

  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [isCurrencyLoaded, setIsCurrencyLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getUserCountryCode()
      .then((countryCode) => {
        if (!isMounted) return;

        const detectedCurrency = countryToCurrencyMap[countryCode] || 'USD';
        setBaseCurrency(detectedCurrency);
        setIsCurrencyLoaded(true);

        if (!selectedCurrency && setCurrency) {
          setCurrency(detectedCurrency);
        }
      })
      .catch(() => {
        if (!isMounted) return;

        setBaseCurrency('USD');
        setIsCurrencyLoaded(true);

        if (!selectedCurrency && setCurrency) {
          setCurrency('USD');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize the display currency
  const displayCurrency = useMemo(
    () => selectedCurrency || baseCurrency,
    [selectedCurrency, baseCurrency]
  );

  // Memoize the converted price calculation
  const formattedPrice = useMemo(() => {
    if (!currenciesRate?.length) return null;

    const baseRate = currenciesRate.find((r) => r.currency === baseCurrency)?.rate || 1;
    const targetRate = currenciesRate.find((r) => r.currency === displayCurrency)?.rate || 1;

    const convertedPrice = (parseFloat(price) / baseRate) * targetRate;

    if (Number.isNaN(convertedPrice)) return null;

    return isCommaSeparated
      ? getFormatNumberWithCommas(Math.ceil(convertedPrice))
      : convertedPrice.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }, [price, baseCurrency, displayCurrency, currenciesRate, isCommaSeparated]);

  if (!isCurrencyLoaded || !currenciesRate?.length) {
    return (
      <span>
        <PriceLoader />
      </span>
    );
  }

  return <span>{`${currencySymbolMap[displayCurrency] || '$'}${formattedPrice}`}</span>;
});

CurrencySwitcher.displayName = 'CurrencySwitcher';

export default CurrencySwitcher;